/**
 * Checks if an object has all the specified required properties and ensures they are not `undefined` or `null`.
 * This function acts as a type guard, allowing TypeScript to infer that the object has the required properties.
 *
 * @template T - The type of the object being checked.
 * @param obj - The object to check for required properties.
 * @param requiredKeys - An array of keys that must be present and defined in the object.
 * @returns {obj is T} - Returns `true` if the object has all required properties; otherwise, `false`.
 *
 * @example
 * const point = { coords: { x: 0, y: 0 }, skIndex: 1, isUpper: true };
 * if (hasRequiredProps(point, ["coords", "skIndex", "isUpper"])) {
 *   // TypeScript now knows point.coords, point.skIndex, and point.isUpper are defined
 * }
 */
export const hasRequiredProps = <T, K extends keyof T>(
  obj: T,
  requiredKeys: K[]
): obj is T & Required<Pick<T, K>> =>
  requiredKeys.every((key) => obj[key] !== undefined && obj[key] !== null);
